// see also
// OptionStrategyDetector.java
// KeyMetricsCalculator.java
// MultiLegStrategy.java from MyMarkets2 / juno project
// see also option pricing Java implementation at ssh://git.financial.com/home/git/tools/optionpricing, e.g. PutCalendarSpread.java

import { isStock, isBuy, isSell } from './legService';

export const StrategyService = {
  detectOptionStrategy
};

/**
 * @param {Object} leg1
 * @param {Object} leg2
 *
 * @return {String} strategy
 */
function detectOptionStrategy(leg1, leg2) {
  let strategy = 'Custom Strategy';

  if (leg1 !== null && leg2 !== null) {
    // multileg strategy: either stock + option or option + option
    if (isStock(leg1)) {
      strategy = detectOptionStrategyForStockWithOption(leg1, leg2);
    } else {
      strategy = detectOptionStrategyForOptionWithOption(leg1, leg2);
    }
  } else if ((leg1 === null || leg2 === null) && !(leg1 === null && leg2 === null)) {
    strategy = detectOptionStrategyForSingleLeg(leg1 !== null ? leg1 : leg2);
  }

  return strategy;
}

/**
 * Return type quantity difference between two quantities
 *
 * @param {Number} quantity1
 * @param {Number} quantity2
 *
 * @return {String} difference
 */
function getQuantityDifferenceType(quantity1, quantity2) {
  if (quantity1 === quantity2) {
    return 'EQUAL';
  } else if (quantity1 === 2 * quantity2) {
    return 'DOUBLE_VALUE';
  } else if (2 * quantity1 === quantity2) {
    return 'HALF_VALUE';
  } else {
    return 'UNDEFINED';
  }
}

/**
 * Detect strategy for a single leg (LONG_STOCK / SHORT_STOCK / LONG_PUT / SHORT_PUT / LONG_CALL / SHORT_CALL)
 *
 * @param {Object} leg
 *
 * @return {String} strategy
 */
function detectOptionStrategyForSingleLeg(leg) {
  if (isStock(leg)) {
    return isBuy(leg) ? 'LONG_STOCK' : 'SHORT_STOCK';
  } else {
    const optionType = leg.option['xopt._TYPE_CP'];

    if (optionType === 'C') {
      return isBuy(leg) ? 'LONG_CALL' : 'SHORT_CALL';
    } else {
      return isBuy(leg) ? 'LONG_PUT' : 'SHORT_PUT';
    }
  }
}

/**
 * Detect strategy for two legs (STOCK + OPTION)
 *
 * @param {Object} leg1
 * @param {Object} leg2
 *
 * @return {String} strategy
 */
function detectOptionStrategyForStockWithOption(leg1, leg2) {
  const optionTypeLeg2 = leg2.option['xopt._TYPE_CP'];

  const lotSizeLeg2 = Math.abs(leg2.option['q.LOT_SIZE_A']);
  if (leg1.quantity !== leg2.quantity * lotSizeLeg2) {
    return 'Custom Strategy';
  }
  if (isBuy(leg1)) {
    if (isSell(leg2) && optionTypeLeg2 === 'C') {
      return 'COVERED_CALL';
    } else if (isBuy(leg2) && optionTypeLeg2 === 'P') {
      return 'PROTECTED_PUT';
    }
  }
  return 'Custom Strategy';
}

/**
 * Detect strategy for two legs (OPTION + OPTION)
 *
 * @param {Object} leg1
 * @param {Object} leg2
 *
 * @return {String} strategy
 */
function detectOptionStrategyForOptionWithOption(leg1, leg2) {
  const optionTypeLeg1 = leg1.option['xopt._TYPE_CP'];
  const optionTypeLeg2 = leg2.option['xopt._TYPE_CP'];

  const actionLeg1 = leg1.action.shortname;
  const actionLeg2 = leg2.action.shortname;

  const maturity1 = new Date(Date.parse(leg1.option['xopt._MATURITY_ISO'])).getTime();
  const maturity2 = new Date(Date.parse(leg2.option['xopt._MATURITY_ISO'])).getTime();
  const expiryDifference = maturity1 - maturity2;
  const strikePriceDifference = leg1.option['xopt._STRIKE'] - leg2.option['xopt._STRIKE'];
  const quantityDifference = leg1.quantity - leg2.quantity;
  const quantityDifferenceType = getQuantityDifferenceType(leg1.quantity, leg2.quantity);

  // legs have different expiry
  if (expiryDifference !== 0) {
    // legs have same type
    if (optionTypeLeg1 === optionTypeLeg2) {
      // legs have same strike
      if (strikePriceDifference === 0) {
        // legs have same strike -> strategy group "CALENDAR_SPREAD"
        return detectCalendarSpreadStrategy(leg1, leg2);
      } else {
        // -> strategy group "DIAGONAL_SPREAD"
        return detectDiagonalSpreadStrategy(leg1, leg2);
      }
    }
  } else {
    // legs have same expiry
    if (optionTypeLeg1 !== optionTypeLeg2) {
      // legs have same quantity
      if (quantityDifference === 0) {
        // legs have same strike
        if (strikePriceDifference === 0) {
          if (actionLeg1 === actionLeg2) {
            // strategy group "STRADDLE"
            switch (actionLeg1) {
              case 'BTO':
                return 'LONG_STRADDLE';
              case 'BTC':
                return 'CLOSING_SHORT_STRADDLE';
              case 'STC':
                return 'CLOSING_LONG_STRADDLE';
              case 'STO':
                return 'SHORT_STRADDLE';
            }
          } else {
            // strategy group "SYNTHETIC_STOCK"
            return detectSyntheticStockStrategy(leg1, leg2);
          }
        } else if (
          (optionTypeLeg1 === 'C' && strikePriceDifference > 0) ||
          (optionTypeLeg1 === 'P' && strikePriceDifference < 0)
        ) {
          // strategy group "STRANGLE"
          if (actionLeg1 === actionLeg2) {
            switch (actionLeg1) {
              case 'BTO':
                return 'LONG_STRANGLE';
              case 'BTC':
                return 'CLOSING_SHORT_STRANGLE';
              case 'STC':
                return 'CLOSING_LONG_STRANGLE';
              case 'STO':
                return 'SHORT_STRANGLE';
              default:
                return 'Custom Strategy';
            }
          }
        }
      }
    } else {
      if (isNaN(quantityDifference) || quantityDifference === 0) {
        const isOpenStrategy = !leg1.action.fullname.includes('Close') && !leg2.action.fullname.includes('Close');
        const isClosingStrategy = leg1.action.fullname.includes('Close') && leg2.action.fullname.includes('Close');

        if (isClosingStrategy || isOpenStrategy) {
          if (optionTypeLeg1 === 'C') {
            //strategy group "VERTICAL_CALL_SPREAD";
            if (isBuy(leg1) && isSell(leg2)) {
              if (strikePriceDifference > 0) {
                return isClosingStrategy ? 'CLOSING_BULL_CALL_SPREAD' : 'BEAR_CALL_SPREAD';
              } else if (strikePriceDifference < 0) {
                return isClosingStrategy ? 'CLOSING_BEAR_CALL_SPREAD' : 'BULL_CALL_SPREAD';
              }
            } else if (isSell(leg1) && isBuy(leg2)) {
              if (strikePriceDifference > 0) {
                return isClosingStrategy ? 'CLOSING_BEAR_CALL_SPREAD' : 'BULL_CALL_SPREAD';
              } else if (strikePriceDifference < 0) {
                return isClosingStrategy ? 'CLOSING_BULL_CALL_SPREAD' : 'BEAR_CALL_SPREAD';
              }
            }
          } else {
            //strategy group "VERTICAL_PUT_SPREAD";
            if (isSell(leg1) && isBuy(leg2)) {
              if (strikePriceDifference > 0) {
                return isClosingStrategy ? 'CLOSING_BEAR_PUT_SPREAD' : 'BULL_PUT_SPREAD';
              } else if (strikePriceDifference < 0) {
                return isClosingStrategy ? 'CLOSING_BULL_PUT_SPREAD' : 'BEAR_PUT_SPREAD';
              }
            } else if (isBuy(leg1) && isSell(leg2)) {
              if (strikePriceDifference > 0) {
                return isClosingStrategy ? 'CLOSING_BULL_PUT_SPREAD' : 'BEAR_PUT_SPREAD';
              } else if (strikePriceDifference < 0) {
                return isClosingStrategy ? 'CLOSING_BEAR_PUT_SPREAD' : 'BULL_PUT_SPREAD';
              }
            }
          }
        }
      } else if (quantityDifferenceType === 'HALF_VALUE' || quantityDifferenceType === 'DOUBLE_VALUE') {
        // is ratio spread
        return detectRatioSpreadStrategy(leg1, leg2);
      }
    }
  }
  return 'Custom Strategy';
}

/**
 * Detect synthetic strategy for two legs (OPTION + OPTION)
 *
 * @param {Object} leg1
 * @param {Object} leg2
 *
 * @return {String} strategy
 */
function detectSyntheticStockStrategy(leg1, leg2) {
  const optionTypeLeg1 = leg1.option['xopt._TYPE_CP'];

  const action1 = leg1.action.shortname;
  const action2 = leg2.action.shortname;

  switch (action1) {
    case 'STO':
      switch (action2) {
        case 'BTO':
          return optionTypeLeg1 === 'C' ? 'SHORT_SYNTHETIC_STOCK' : 'LONG_SYNTHETIC_STOCK';
        default:
          return 'Custom Strategy';
      }
    case 'BTO':
      switch (action2) {
        case 'STO':
          return optionTypeLeg1 === 'C' ? 'LONG_SYNTHETIC_STOCK' : 'SHORT_SYNTHETIC_STOCK';
        default:
          return 'Custom Strategy';
      }
    case 'STC':
      switch (action2) {
        case 'BTC':
          return optionTypeLeg1 === 'C' ? 'CLOSING_LONG_SYNTHETIC_STOCK' : 'CLOSING_SHORT_SYNTHETIC_STOCK';
        default:
          return 'Custom Strategy';
      }
    case 'BTC':
      switch (action2) {
        case 'STC':
          return optionTypeLeg1 === 'C' ? 'CLOSING_SHORT_SYNTHETIC_STOCK' : 'CLOSING_LONG_SYNTHETIC_STOCK';
        default:
          return 'Custom Strategy';
      }
    default:
      return 'Custom Strategy';
  }
}

/**
 * Detect calendar spread strategy for two legs (OPTION + OPTION)
 *
 * @param {Object} leg1
 * @param {Object} leg2
 *
 * @return {String} strategy
 */
function detectCalendarSpreadStrategy(leg1, leg2) {
  const optionTypeLeg1 = leg1.option['xopt._TYPE_CP'];
  const optionTypeLeg2 = leg2.option['xopt._TYPE_CP'];

  const maturity1 = new Date(Date.parse(leg1.option['xopt._MATURITY_ISO'])).getTime();
  const maturity2 = new Date(Date.parse(leg2.option['xopt._MATURITY_ISO'])).getTime();
  const expiryDifference = maturity1 - maturity2;
  const strikePriceDifference = leg1.option['xopt._STRIKE'] - leg2.option['xopt._STRIKE'];

  const isClosingAction = leg1.action.fullname.includes('Close') && leg2.action.fullname.includes('Close');

  const quantityDifference = leg1.quantity - leg2.quantity;
  if (quantityDifference !== 0) {
    return 'Custom Strategy';
  }

  if (isSell(leg1) && isBuy(leg2)) {
    if (expiryDifference < 0) {
      if (optionTypeLeg1 === 'C' && optionTypeLeg2 === 'C' && strikePriceDifference === 0) {
        return isClosingAction ? 'CLOSING_SHORT_CALL_CALENDAR' : 'LONG_CALL_CALENDAR';
      } else if (optionTypeLeg1 === 'P' && optionTypeLeg2 === 'P' && strikePriceDifference === 0) {
        return isClosingAction ? 'CLOSING_SHORT_PUT_CALENDAR' : 'LONG_PUT_CALENDAR';
      } else {
        return 'Custom Strategy';
      }
    } else {
      if (optionTypeLeg1 === 'C' && optionTypeLeg2 === 'C' && strikePriceDifference === 0) {
        return isClosingAction ? 'CLOSING_LONG_CALL_CALENDAR' : 'SHORT_CALL_CALENDAR';
      } else if (optionTypeLeg1 === 'P' && optionTypeLeg2 === 'P' && strikePriceDifference === 0) {
        return isClosingAction ? 'CLOSING_LONG_PUT_CALENDAR' : 'SHORT_PUT_CALENDAR';
      } else {
        return 'Custom Strategy';
      }
    }
  } else if (isBuy(leg1) && isSell(leg2)) {
    if (expiryDifference < 0) {
      if (optionTypeLeg1 === 'C' && optionTypeLeg2 === 'C' && strikePriceDifference === 0) {
        return 'SHORT_CALL_CALENDAR';
      } else if (optionTypeLeg1 === 'P' && optionTypeLeg2 === 'P' && strikePriceDifference === 0) {
        return 'SHORT_PUT_CALENDAR';
      } else {
        return 'Custom Strategy';
      }
    } else {
      if (optionTypeLeg1 === 'C' && optionTypeLeg2 === 'C' && strikePriceDifference === 0) {
        return 'LONG_CALL_CALENDAR';
      } else if (optionTypeLeg1 === 'P' && optionTypeLeg2 === 'P' && strikePriceDifference === 0) {
        return 'LONG_PUT_CALENDAR';
      } else {
        return 'Custom Strategy';
      }
    }
  } else {
    return 'Custom Strategy';
  }
}

/**
 * Detect diagonal spread strategy for two legs (OPTION + OPTION)
 *
 * @param {Object} leg1
 * @param {Object} leg2
 *
 * @return {String} strategy
 */
function detectDiagonalSpreadStrategy(leg1, leg2) {
  const optionTypeLeg1 = leg1.option['xopt._TYPE_CP'];
  const optionTypeLeg2 = leg2.option['xopt._TYPE_CP'];

  const maturity1 = new Date(Date.parse(leg1.option['xopt._MATURITY_ISO'])).getTime();
  const maturity2 = new Date(Date.parse(leg2.option['xopt._MATURITY_ISO'])).getTime();
  const expiryDifference = maturity1 - maturity2;
  const strikePriceDifference = leg1.option['xopt._STRIKE'] - leg2.option['xopt._STRIKE'];

  const isClosingAction = leg1.action.fullname.includes('Close') && leg2.action.fullname.includes('Close');

  //const quantityDifference = leg1.quantity - leg2.quantity;

  // leg1 should always be the front-month, leg2 the back-month option
  // if not we switch leg1 and leg2
  if (expiryDifference > 0) {
    return detectDiagonalSpreadStrategy(leg2, leg1);
  }

  if (isSell(leg1) && isBuy(leg2)) {
    // assuming expiryDifference < 0, leg1 is front-month option, leg2 is back-month option
    if (optionTypeLeg1 === 'C' && optionTypeLeg2 === 'C') {
      if (strikePriceDifference < 0) {
        // X1 < X2
        return isClosingAction ? 'DIAGONAL_BULL_CALL_SPREAD' : 'DIAGONAL_BEAR_CALL_SPREAD';
      } else if (strikePriceDifference > 0) {
        // X1 > X2
        return isClosingAction ? 'DIAGONAL_BEAR_CALL_SPREAD' : 'DIAGONAL_BULL_CALL_SPREAD';
      } else if (strikePriceDifference === 0) {
        return 'LONG_CALL_CALENDAR';
      }
    } else if (optionTypeLeg1 === 'P' && optionTypeLeg2 === 'P') {
      if (strikePriceDifference < 0) {
        // X1 < X2
        return isClosingAction ? 'DIAGONAL_BULL_PUT_SPREAD' : 'DIAGONAL_BEAR_PUT_SPREAD';
      } else if (strikePriceDifference > 0) {
        // X1 > X2
        return isClosingAction ? 'DIAGONAL_BEAR_PUT_SPREAD' : 'DIAGONAL_BULL_PUT_SPREAD';
      } else if (strikePriceDifference === 0) {
        return 'LONG_PUT_CALENDAR';
      }
    } else {
      return 'Custom Strategy';
    }
  } else if (isBuy(leg1) && isSell(leg2)) {
    // assuming expiryDifference < 0, leg1 is front-month option, leg2 is back-month option
    // In a Diagonal Spread, the front-month option is always sold, the back-month option is bought.
    // If the front-month is bought, we do not treat this as a "Diagonal Call/Put Spread"
    if (isClosingAction) {
      if (strikePriceDifference < 0) {
        return optionTypeLeg1 === 'C' ? 'CLOSING_DIAGONAL_BEAR_CALL_SPREAD' : 'CLOSING_DIAGONAL_BEAR_PUT_SPREAD';
      } else if (strikePriceDifference > 0) {
        return optionTypeLeg1 === 'C' ? 'CLOSING_DIAGONAL_BULL_CALL_SPREAD' : 'CLOSING_DIAGONAL_BULL_PUT_SPREAD';
      }
    }
    return 'Custom Strategy';
  } else {
    return 'Custom Strategy';
  }
}

/**
 * Detect ratio spread strategy for two legs (OPTION + OPTION)
 *
 * @param {Object} leg1
 * @param {Object} leg2
 *
 * @return {String} strategy
 */
function detectRatioSpreadStrategy(leg1, leg2) {
  const optionTypeLeg1 = leg1.option['xopt._TYPE_CP'];
  const optionTypeLeg2 = leg2.option['xopt._TYPE_CP'];

  const strikePriceDifference = leg1.option['xopt._STRIKE'] - leg2.option['xopt._STRIKE'];
  const quantityDifferenceType = getQuantityDifferenceType(leg1.quantity, leg2.quantity);

  const isClosingAction = leg1.action.fullname.includes('Close') && leg2.action.fullname.includes('Close');
  if (optionTypeLeg1 === 'C' && optionTypeLeg2 === 'C') {
    // StrategyGroup: RATIO_CALL_SPREAD : CALL_RATIO_SPREAD or CALL_BACK_SPREAD
    if (isBuy(leg1) && isSell(leg2)) {
      if (strikePriceDifference < 0 && quantityDifferenceType === 'HALF_VALUE') {
        return isClosingAction ? 'CLOSING_CALL_BACK_SPREAD' : 'CALL_RATIO_SPREAD';
      } else if (strikePriceDifference > 0 && quantityDifferenceType === 'DOUBLE_VALUE') {
        return isClosingAction ? 'CLOSING_CALL_RATIO_SPREAD' : 'CALL_BACK_SPREAD';
      }
    } else if (isSell(leg1) && isBuy(leg2)) {
      if (strikePriceDifference > 0 && quantityDifferenceType === 'DOUBLE_VALUE') {
        return isClosingAction ? 'CLOSING_CALL_BACK_SPREAD' : 'CALL_RATIO_SPREAD';
      } else if (strikePriceDifference < 0 && quantityDifferenceType === 'HALF_VALUE') {
        return isClosingAction ? 'CLOSING_CALL_RATIO_SPREAD' : 'CALL_BACK_SPREAD';
      }
    }
  } else if (optionTypeLeg1 === 'P' && optionTypeLeg2 === 'P') {
    // StrategyGroup: RATIO_PUT_SPREAD : PUT_RATIO_SPREAD or PUT_BACK_SPREAD
    if (isBuy(leg1) && isSell(leg2)) {
      if (strikePriceDifference < 0 && quantityDifferenceType === 'DOUBLE_VALUE') {
        return isClosingAction ? 'CLOSING_PUT_RATIO_SPREAD' : 'PUT_BACK_SPREAD';
      } else if (strikePriceDifference > 0 && quantityDifferenceType === 'HALF_VALUE') {
        return isClosingAction ? 'CLOSING_PUT_BACK_SPREAD' : 'PUT_RATIO_SPREAD';
      }
    } else if (isSell(leg1) && isBuy(leg2)) {
      if (strikePriceDifference > 0 && quantityDifferenceType === 'HALF_VALUE') {
        return isClosingAction ? 'CLOSING_PUT_RATIO_SPREAD' : 'PUT_BACK_SPREAD';
      } else if (strikePriceDifference < 0 && quantityDifferenceType === 'DOUBLE_VALUE') {
        return isClosingAction ? 'CLOSING_PUT_BACK_SPREAD' : 'PUT_RATIO_SPREAD';
      }
    }
  }
  return 'Custom Strategy';
}
